import React, { useCallback, useEffect, useState } from "react";
import useApi from "@/hooks/useApi";
import useAdminFeatures from "@/hooks/useAdminFeatures";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity, PrimaryBlueButton } from "@velaro/velaro-component-lib";
import { PremiumTag } from "@/components/PremiumTag";
import ProactiveRuleEditor from "./ProactiveRuleEditor";
import ProactiveAnalytics from "./ProactiveAnalytics";

interface ProactiveRule {
  id: number;
  siteId: number;
  name: string;
  ruleType: string;
  signalConfig: string;
  pageTargeting: string;
  knowledgeSources: string;
  messageStrategy: string;
  messageTemplate: string | null;
  aiPromptOverride: string | null;
  cooldownMinutes: number;
  priority: number;
  maxPerSession: number;
  maxPerVisitor: number;
  isEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

const RULE_TYPE_LABELS: Record<string, string> = {
  page_context: "Page Context",
  cart_state: "Cart State",
  return_visitor: "Return Visitor",
  time_based: "Time-Based",
  scroll_depth: "Scroll Depth",
  custom: "Custom",
};

const STRATEGY_LABELS: Record<string, string> = {
  ai_generated: "AI Generated",
  template: "Template",
  hybrid: "Hybrid",
};

export default function ProactiveEngagement() {
  const api = useApi();
  const features = useAdminFeatures();
  const { displayToast } = useToastAlert();

  const [rules, setRules] = useState<ProactiveRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingRule, setEditingRule] = useState<ProactiveRule | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [activeTab, setActiveTab] = useState<"rules" | "analytics">("rules");

  const loadRules = useCallback(async () => {
    try {
      const res = await api.admin.get("ProactiveEngagement");
      const data = await res.json();
      setRules(Array.isArray(data) ? data : []);
    } catch {
      setRules([]);
    } finally {
      setLoading(false);
    }
  }, [api.admin]);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  const handleToggle = async (rule: ProactiveRule) => {
    try {
      await api.admin.put(`ProactiveEngagement/${rule.id}/toggle`);
      await loadRules();
    } catch {
      displayToast(Severity.Error, "Failed to toggle rule");
    }
  };

  const handleDelete = async (rule: ProactiveRule) => {
    if (!confirm(`Delete rule "${rule.name}"?`)) return;
    try {
      await api.admin.delete(`ProactiveEngagement/${rule.id}`);
      displayToast(Severity.Success, "Rule deleted");
      await loadRules();
    } catch {
      displayToast(Severity.Error, "Failed to delete rule");
    }
  };

  if (!features.enableProactiveEngagement) {
    return (
      <div className="flex flex-col items-center justify-center p-12 text-center gap-4">
        <i className="fa-solid fa-bolt text-4xl text-primary-hover" />
        <div className="text-xl font-semibold">Proactive Engagement Engine</div>
        <PremiumTag />
        <p className="text-sm text-slate-400 max-w-md">
          Anticipate what visitors need before they ask. The Proactive Engagement Engine
          uses your unified knowledge index to generate contextual, page-aware messages
          powered by 7 data sources.
        </p>
        <div className="text-sm text-slate-500 mt-4">
          Contact your account manager to enable this feature.
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Tabs */}
      <div className="flex items-center gap-4 mb-6 border-b border-slate-200 dark:border-slate-700">
        <button
          className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "rules"
              ? "border-primary text-primary dark:text-primary-hover"
              : "border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400"
          }`}
          onClick={() => setActiveTab("rules")}
        >
          Rules
        </button>
        <button
          className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "analytics"
              ? "border-primary text-primary dark:text-primary-hover"
              : "border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400"
          }`}
          onClick={() => setActiveTab("analytics")}
        >
          Analytics
        </button>
      </div>

      {activeTab === "analytics" && <ProactiveAnalytics />}

      {activeTab === "rules" && (
        <>
          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <div>
              <h1 className="text-xl font-semibold text-slate-800">Proactive Rules</h1>
              <p className="text-sm text-slate-500 mt-0.5">
                Create rules that trigger contextual AI messages based on visitor behavior and page context.
                Each rule defines <strong>when</strong> to engage (signals) and <strong>what</strong> to say (knowledge sources).
              </p>
              {features.maxProactiveRules > 0 && (
                <p className="text-xs text-slate-400 mt-1">
                  {rules.length} / {features.maxProactiveRules} rules used
                </p>
              )}
            </div>
            <div className="shrink-0 ml-4">
              <PrimaryBlueButton
                onClick={() => { setEditingRule(null); setShowCreate(true); }}
                label="+ New Rule"
                disabled={features.maxProactiveRules > 0 && rules.length >= features.maxProactiveRules}
              />
            </div>
          </div>

          {/* Rules list */}
          {loading ? (
            <div className="text-center py-12 text-slate-400">Loading...</div>
          ) : rules.length === 0 ? (
            <div className="text-center py-12 border border-dashed border-slate-300 dark:border-slate-600 rounded-lg">
              <i className="fa-solid fa-bolt text-3xl text-slate-300 dark:text-slate-600 mb-3" />
              <p className="text-slate-500 dark:text-slate-400 mb-2">No proactive rules yet</p>
              <p className="text-sm text-slate-400 dark:text-slate-500 mb-4">
                Create your first rule to start engaging visitors proactively with contextual AI messages.
              </p>
              <PrimaryBlueButton
                onClick={() => { setEditingRule(null); setShowCreate(true); }}
                label="+ Create First Rule"
              />
            </div>
          ) : (
            <div className="space-y-3">
              {rules.map((rule) => {
                const signalConfig = safeParseJson(rule.signalConfig);
                const pageTargeting = safeParseJson(rule.pageTargeting);
                const targetingLabel = Array.isArray(pageTargeting) && pageTargeting.length > 0
                  ? pageTargeting.map((t: { type: string; value: string }) =>
                      t.type === "all_pages" ? "All pages" : `${t.type}: ${t.value}`
                    ).join(", ")
                  : "All pages";

                return (
                  <div
                    key={rule.id}
                    className={`border rounded-lg p-4 transition-all ${
                      rule.isEnabled
                        ? "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800"
                        : "border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 opacity-60"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-medium text-sm truncate">{rule.name}</h3>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-primary-bg text-primary-active dark:bg-cornflower-blue-900/30 dark:text-cornflower-blue-300 shrink-0">
                            {RULE_TYPE_LABELS[rule.ruleType] ?? rule.ruleType}
                          </span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300 shrink-0">
                            {STRATEGY_LABELS[rule.messageStrategy] ?? rule.messageStrategy}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-slate-400">
                          <span title="Page targeting">
                            <i className="fa-solid fa-crosshairs mr-1" />
                            {targetingLabel}
                          </span>
                          <span title="Cooldown">
                            <i className="fa-regular fa-clock mr-1" />
                            {rule.cooldownMinutes}m cooldown
                          </span>
                          <span title="Priority">
                            <i className="fa-solid fa-arrow-up-1-9 mr-1" />
                            Priority {rule.priority}
                          </span>
                          {signalConfig?.dwellTimeSeconds && (
                            <span title="Dwell time trigger">
                              <i className="fa-solid fa-hourglass-half mr-1" />
                              {signalConfig.dwellTimeSeconds}s dwell
                            </span>
                          )}
                          {signalConfig?.scrollDepthPercent && (
                            <span title="Scroll depth trigger">
                              <i className="fa-solid fa-arrows-up-down mr-1" />
                              {signalConfig.scrollDepthPercent}% scroll
                            </span>
                          )}
                          {signalConfig?.cartValueMin && (
                            <span title="Cart value trigger">
                              <i className="fa-solid fa-cart-shopping mr-1" />
                              ${signalConfig.cartValueMin}+
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          onClick={() => handleToggle(rule)}
                          className={`w-10 h-5 rounded-full relative transition-colors ${
                            rule.isEnabled ? "bg-green-500" : "bg-slate-300 dark:bg-slate-600"
                          }`}
                          title={rule.isEnabled ? "Disable" : "Enable"}
                        >
                          <span
                            className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                              rule.isEnabled ? "translate-x-5" : "translate-x-0.5"
                            }`}
                          />
                        </button>
                        <button
                          onClick={() => { setEditingRule(rule); setShowCreate(true); }}
                          className="text-slate-400 hover:text-primary p-1"
                          title="Edit"
                        >
                          <i className="fa-solid fa-pen-to-square" />
                        </button>
                        <button
                          onClick={() => handleDelete(rule)}
                          className="text-slate-400 hover:text-red-500 p-1"
                          title="Delete"
                        >
                          <i className="fa-solid fa-trash" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Editor modal */}
      {showCreate && (
        <ProactiveRuleEditor
          rule={editingRule}
          onClose={() => { setShowCreate(false); setEditingRule(null); }}
          onSaved={() => { setShowCreate(false); setEditingRule(null); loadRules(); }}
        />
      )}
    </div>
  );
}

function safeParseJson(json: string | null | undefined): any {
  if (!json) return {};
  try { return JSON.parse(json); } catch { return {}; }
}
